import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import subscriptionService from '../services/subscriptionService';
import { genesisApi } from '../services/genesisApi';
import { useAuth } from './AuthContext';

// Types
interface Subscription {
  id: number;
  plan: {
    id: number;
    name: string;
    features: string[];
  };
  is_active: boolean;
  created_at: string;
  expires_at: string;
  user: number;
}

export interface GenesisQuota {
  user_id: number;
  plan: 'trial' | 'genesis_basic' | 'genesis_pro' | 'genesis_enterprise';
  subscription_status: 'active' | 'expired' | 'cancelled' | 'suspended';
  quota_limit: number;
  quota_used: number;
  quota_reset_date: string;
}

interface SubscriptionContextType {
  // State
  currentSubscription: Subscription | null;
  subscriptions: Subscription[];
  isLoading: boolean;
  error: string | null;

  // Actions
  setActiveSubscription: (subscriptionId: number) => Promise<void>;
  refreshSubscriptions: () => Promise<void>;
  hasFeature: (featureName: string) => boolean;
  hasPlan: (planName: string) => boolean;

  // Computed
  isSubscriptionActive: boolean;
  canManageFAQs: boolean;
  canConfigureWhatsApp: boolean;
  canAccessGenesis: boolean;
  canAccessSkillsCoach: boolean;
  subscriptionTier: 'free' | 'basic' | 'premium' | 'enterprise';

  // Genesis Specific
  genesisQuota: GenesisQuota | null;
  refreshGenesisQuota: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

interface SubscriptionProviderProps {
  children: ReactNode;
}

const isWhatsAppPlan = (subscription: Subscription): boolean =>
  subscription.plan.name.toLowerCase().includes('whatsapp');

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [currentSubscription, setCurrentSubscription] = useState<Subscription | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [genesisQuota, setGenesisQuota] = useState<GenesisQuota | null>(null);

  // Charger les abonnements au montage et quand user change
  useEffect(() => {
    if (isAuthenticated && user) {
      loadUserSubscriptions();
    } else {
      // Reset state si déconnecté
      setCurrentSubscription(null);
      setSubscriptions([]);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (isAuthenticated && user) {
      loadGenesisQuota();
    }
  }, [isAuthenticated, user]);

  const loadUserSubscriptions = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await subscriptionService.getUserSubscriptions();
      console.log('Subscription API response:', response.data);

      // Extraire les résultats depuis la structure paginée
      const subscriptionsData = response.data.results || response.data;
      setSubscriptions(subscriptionsData);

      const activeSubscriptions = subscriptionsData.filter(
        (sub: Subscription) => sub.is_active
      );
      const savedSubscriptionId = Number(localStorage.getItem('activeSubscriptionId'));
      const savedWhatsAppSubscription = activeSubscriptions.find(
        (sub: Subscription) => sub.id === savedSubscriptionId && isWhatsAppPlan(sub)
      );
      const whatsappSubscription = activeSubscriptions.find(isWhatsAppPlan);
      const selectedSubscription =
        savedWhatsAppSubscription || whatsappSubscription || activeSubscriptions[0] || null;

      if (selectedSubscription) {
        setCurrentSubscription(selectedSubscription);
        console.log('Active subscription selected:', selectedSubscription);
      } else {
        setCurrentSubscription(null);
      }

    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des abonnements');
      console.error('Subscription loading error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadGenesisQuota = async () => {
    if (!user?.id) return;
    try {
      const quota = await genesisApi.getQuotaStatus(user.id);
      setGenesisQuota(quota);
    } catch (err) {
      console.error('Failed to load Genesis quota', err);
    }
  };

  const setActiveSubscription = async (subscriptionId: number) => {
    const subscription = subscriptions.find(sub => sub.id === subscriptionId);
    if (subscription) {
      setCurrentSubscription(subscription);

      // Optionnel: persister le choix en localStorage
      localStorage.setItem('activeSubscriptionId', subscriptionId.toString());
    }
  };

  const refreshSubscriptions = async () => {
    await loadUserSubscriptions();
    await loadGenesisQuota();
  };

  const refreshGenesisQuota = async () => {
    await loadGenesisQuota();
  };

  const hasFeature = (featureName: string): boolean => {
    if (!currentSubscription) return false;
    return currentSubscription.plan.features?.includes(featureName) || false;
  };

  const hasPlan = (planName: string): boolean => {
    if (!currentSubscription) return false;
    return currentSubscription.plan.name === planName;
  };

  // Computed values
  const isSubscriptionActive = currentSubscription?.is_active ?? false;
  const hasCurrentWhatsAppPlan = Boolean(currentSubscription && isWhatsAppPlan(currentSubscription));
  const canManageFAQs = hasCurrentWhatsAppPlan && isSubscriptionActive;
  const canConfigureWhatsApp = hasCurrentWhatsAppPlan && isSubscriptionActive;

  const hasGenesisPlan = (() => {
    if (!currentSubscription) return false;
    const planName = currentSubscription.plan.name.toLowerCase();
    const isGenesis = planName.includes('genesis');
    const isTier = planName.includes('basic') || planName.includes('pro') || planName.includes('enterprise');
    return isGenesis && isTier;
  })();

  const canAccessGenesis = hasGenesisPlan && isSubscriptionActive;

  const hasSkillsCoachPlan = (() => {
    if (!currentSubscription) return false;
    const planName = currentSubscription.plan.name.toLowerCase();
    return planName.includes('skills coach') || planName.includes('skills-coach');
  })();

  const canAccessSkillsCoach = hasSkillsCoachPlan && isSubscriptionActive;

  const subscriptionTier = (() => {
    if (!currentSubscription) return 'free';
    const planName = currentSubscription.plan.name.toLowerCase();
    if (planName.includes('enterprise')) return 'enterprise';
    if (planName.includes('premium')) return 'premium';
    if (planName.includes('faq') || planName.includes('basic')) return 'basic';
    return 'free';
  })();

  const contextValue: SubscriptionContextType = {
    // State
    currentSubscription,
    subscriptions,
    isLoading,
    error,

    // Actions
    setActiveSubscription,
    refreshSubscriptions,
    hasFeature,
    hasPlan,

    // Computed
    isSubscriptionActive,
    canManageFAQs,
    canConfigureWhatsApp,
    canAccessGenesis,
    canAccessSkillsCoach,
    subscriptionTier,
    genesisQuota,
    refreshGenesisQuota,
  };

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
};

// Hook pour utiliser le context
export const useSubscriptionContext = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscriptionContext must be used within a SubscriptionProvider');
  }
  return context;
};
